import { Layout } from "providers/Layout";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Button,
  ButtonSize,
  ButtonVariant,
  IconButton,
  IconButtonSize,
  Show
} from "shared/components";
import { BaseInput } from "shared/components/forms/BaseInput";
// check-deprecations-disable-next-line
import { Tabs } from "shared/components/Tabs";
import { InputSize } from "shared/components/forms/DateInput";
import {
  AlertCircle,
  CheckDone01,
  FilterLines,
  Upload01,
  UserRight01
} from "untitledui-js-base";
import { Settings02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { AccountStatusVariant, PaymentStatuses } from "./constants";
import { Link } from "react-router-dom";
import { SubscribersTable } from "./components/SubscribersTable";
import { ReportModal } from "./components/ReportModal";
import { Pagination } from "shared/components/Pagination";
import { CSVLink } from "react-csv";
import { CSVUploadBanner } from "./components/CSVUploadBanner";
import { FilterDrawer } from "./components/FilterDrawer";
import { useAuthContext } from "providers/Auth";
import { useCombinedRentReportingController } from "./controllers";
import { ProgressUpdate } from "shared/utils/fetch-pages-in-parallel";
import { Download04Icon } from "@hugeicons-pro/core-stroke-rounded";
import { getDisplayAccountStatus, getRvStatusText } from "./utils";
import { DateTime } from "luxon";
import { getErrorMessage } from "utils/error";
import { isManualEnrollmentDisabledForPms } from "utils/manualEnrollmentRestrictions";
import { toast } from "react-toastify";
import { Redirect } from "shared/components/Redirect";
import { formatToCurrency } from "shared/utils";
import { CSVRenterRow } from "./types";

export const RentReportingView = () => {
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    moveInDateFrom: "",
    moveInDateTo: "",
    moveOutDateFrom: "",
    moveOutDateTo: "",
    leaseStartDateFrom: "",
    leaseStartDateTo: "",
    selectedProperties: [] as number[],
    selectedStates: [] as string[],
    selectedEnrollmentStatuses: [] as string[]
  });

  const {
    subscribersMeta,
    needToReportCountMeta,
    pendingCountMeta,
    searchParams,
    setSearchParams,
    debouncedParams,
    reportAllRenters,
    sorted,
    setSorted,
    isReportingAllRenters,
    isReportingSingleRenter,
    reportSingleRenter,
    getFilteredSubscribersToCSV
  } = useCombinedRentReportingController(filters);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectDefaultValue, setSelectDefaultValue] = useState<PaymentStatuses>(
    PaymentStatuses.NO_DATA
  );
  const [selectValues, setSelectValues] = useState<
    { id: number; action: string | number }[]
  >([]);
  const previousMonth = DateTime.now().minus({ months: 1 }).toFormat("MMMM");
  const [csvData, setCsvData] = useState<
    {
      "Customer ID": string;
      Renter: string;
      "Renter email": string;
      "Account status": string;
      "Property name": string;
      "Property address": string;
      "Rent amount": string;
      [key: string]: string | undefined;
    }[]
  >([]);

  const ref = useRef<HTMLAnchorElement>(null);

  const { type, partner, permissions } = useAuthContext();

  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [downloadProgress, setDownloadProgress] =
    useState<ProgressUpdate | null>(null);

  useEffect(() => {
    if (
      type.isSDK ||
      ((needToReportCountMeta.data ?? 0) === 0 &&
        searchParams.type === AccountStatusVariant.NeedToReport)
    )
      setSearchParams({ ...searchParams, type: AccountStatusVariant.All });
  }, [type.isSDK, needToReportCountMeta.data]);

  const paidOnTime = useMemo(() => {
    const needToReportCount = needToReportCountMeta.data ?? 0;
    if (selectValues.length === 0)
      return selectDefaultValue === PaymentStatuses.PAID_ON_TIME
        ? needToReportCount
        : 0;
    else
      return selectDefaultValue === PaymentStatuses.PAID_ON_TIME
        ? needToReportCount - selectValues.length
        : selectValues.filter((v) => v.action === PaymentStatuses.PAID_ON_TIME)
            .length || 0;
  }, [selectValues, selectDefaultValue, needToReportCountMeta.data]);

  const handleReportAll = () => {
    const verifications = selectValues.map((item) => ({
      ...item,
      action:
        item.action === PaymentStatuses.PAID_ON_TIME ||
        item.action === PaymentStatuses.NO_DATA
          ? item.action
          : PaymentStatuses.DELINQUENCY,
      months_delinquency:
        item.action === PaymentStatuses.PAID_ON_TIME ||
        item.action === PaymentStatuses.NO_DATA
          ? undefined
          : item.action
    }));
    reportAllRenters({ verifications, defaultAction: selectDefaultValue });
    setSelectValues([]);
    setShowReportModal(false);
  };

  const isNeedToReportTab =
    searchParams.type === AccountStatusVariant.NeedToReport;
  const isAllTab = searchParams.type === AccountStatusVariant.All;
  const isPendingTab = searchParams.type === AccountStatusVariant.Pending;

  const handleTabChange = (t: string) => {
    const isNewTabPendingOrAll =
      t === AccountStatusVariant.Pending || t === AccountStatusVariant.All;
    if (!isNewTabPendingOrAll)
      setFilters((prev) =>
        prev.selectedEnrollmentStatuses.length === 0
          ? prev
          : { ...prev, selectedEnrollmentStatuses: [] }
      );
    setSearchParams({ ...searchParams, page: "1", type: t });
  };

  const hasNeedToReport = (needToReportCountMeta.data ?? 0) > 0;

  const hasSubscribers =
    Array.isArray(subscribersMeta.data?.items) &&
    subscribersMeta.data.items.length > 0;

  // Count active filters
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (filters.selectedEnrollmentStatuses.length > 0) count++;
    if (filters.selectedProperties.length > 0) count++;
    if (filters.selectedStates.length > 0) count++;
    if (filters.moveInDateFrom) count++;
    if (filters.moveInDateTo) count++;
    if (filters.moveOutDateFrom) count++;
    if (filters.moveOutDateTo) count++;
    if (filters.leaseStartDateFrom) count++;
    if (filters.leaseStartDateTo) count++;
    return count;
  }, [filters]);

  const showBulkActions: boolean = useMemo(
    () =>
      Boolean(
        !type.isSDK &&
        (isNeedToReportTab || isAllTab) &&
        hasNeedToReport &&
        hasSubscribers
      ),
    [type.isSDK, isNeedToReportTab, isAllTab, hasNeedToReport, hasSubscribers]
  );

  const sanitizeCsvValue = (value: string | undefined): string => {
    if (!value) return "--";
    // Replace newlines and carriage returns with spaces to prevent CSV row breaks
    return value.replace(/[\r\n]+/g, " ").trim();
  };

  const handleDownloadCSV = async () => {
    try {
      const renters = await getFilteredSubscribersToCSV(
        filters,
        setDownloadProgress
      );
      if (!renters.length) return;
      setCsvData(
        renters.map((renter) => {
          const csvRow: CSVRenterRow = {
            "Customer ID": renter.id.toString() || "--",
            Renter: renter.name || "--",
            "Renter email": renter.email || "--",
            "Property name": renter.property || "--",
            "Property address": sanitizeCsvValue(renter.address) || "--",
            "Rent amount": renter.current_month_payment
              ? formatToCurrency(renter.current_month_payment).replace(/,/g, "")
              : "--",
            "Enrollment ID":
              renter.active_enrollment?.reporting_account_enrollment_id.toString() ||
              "--",
            "Account status":
              getDisplayAccountStatus(renter).toString().replace("_", " ") ||
              "--",
            "Enrollment created at": renter.active_enrollment?.created_at
              ? DateTime.fromISO(renter.active_enrollment.created_at).toFormat(
                  "MM-dd-yyyy"
                )
              : "--",
            "Unenrolled at": renter.active_enrollment?.unenrollment_date
              ? DateTime.fromISO(
                  renter.active_enrollment.unenrollment_date
                ).toFormat("MM-dd-yyyy")
              : "--",
            "Consent type": renter.active_enrollment?.consent_type || "--",
            [`${previousMonth} status`]: sanitizeCsvValue(
              getRvStatusText(renter.rv_status ?? null)
            )
          };
          return csvRow;
        })
      );
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  useEffect(() => {
    if (csvData.length)
      // @ts-ignore
      ref.current?.link.click() && ref.current.link.blur();
  }, [csvData]);

  if (!permissions.canViewRentReporting) return <Redirect href="/" />;

  if (subscribersMeta.isError)
    return (
      <Layout>
        <div>
          <h1 className="mb-4 text-lg font-medium">
            Error loading subscribers.
          </h1>
          <p className="text-base font-medium">
            {subscribersMeta.error.message}
          </p>
        </div>
      </Layout>
    );

  return (
    <Layout
      headerClassName="py-0"
      header={
        <>
          <div className="px-6 py-6 text-xl font-bold border-b border-alpha-100 text-content-1000 flex justify-between items-center">
            <h1 className="text-2xl text-content-1000 font-bold mb-3">
              Rent reporting
            </h1>
            <div className="flex gap-2">
              {((permissions.canSubscribeRenters && type.isManual) ||
                (type.isPMS && type.canManageEntitiesDirectly) ||
                (type.isPMS && type.isBoomOps)) &&
              !isManualEnrollmentDisabledForPms(
                partner?.property_management_system?.name
              ) ? (
                <div className="flex gap-2">
                  <Link to="/rent-reporting/add">
                    <Button
                      Icon={UserRight01}
                      className="stroke-content-300"
                      size={ButtonSize.SM}
                    >
                      Subscribe manually
                    </Button>
                  </Link>
                  <Link to="/rent-reporting/upload">
                    <Button
                      Icon={Upload01}
                      size={ButtonSize.SM}
                      className="stroke-content-300"
                    >
                      Import data
                    </Button>
                  </Link>
                </div>
              ) : null}
              {permissions.canSubscribeRenters ? (
                <Link to="/rent-reporting/settings">
                  <Button
                    variant={ButtonVariant.BASE}
                    size={ButtonSize.SM}
                    HugeIcon={Settings02Icon}
                  >
                    Settings
                  </Button>
                </Link>
              ) : null}
            </div>
          </div>
          <CSVUploadBanner />
          {(needToReportCountMeta.data ?? 0) > 0 && !type.isSDK ? (
            <div className="flex items-center gap-6 border-b border-alpha-100 bg-background-1000 px-6 py-5">
              <AlertCircle size="24" className="stroke-primary-500" />
              <div className="h-10 w-px bg-alpha-40" />
              <div>
                <p className="text-sm font-medium text-content-400">
                  Please review and report the status of rent payments.
                </p>
                <p className="text-sm font-medium text-content-400">
                  Past months will be reported as &apos;No Data&apos; if you
                  don&apos;t report rent payments within 30 days
                </p>
              </div>
            </div>
          ) : null}
        </>
      }
    >
      <div className="flex justify-between items-center mb-4 min-w-0">
        <div className="flex flex-col justify-between gap-4 min-w-0 flex-1">
          <div className="flex gap-2">
            <BaseInput
              id="search"
              placeholder="Search"
              value={searchParams.search}
              onChange={(value) =>
                setSearchParams({
                  ...searchParams,
                  page: "1",
                  search: value
                })
              }
              className="!mb-0 max-w-[271px]"
              size={InputSize.SM}
            />
            <div className="flex items-center gap-2">
              <IconButton
                Icon={FilterLines}
                size={IconButtonSize.SM}
                onClick={() => setShowFilters(true)}
              />
              {activeFiltersCount > 0 && (
                <span className="text-xs font-medium text-content-1000">
                  {activeFiltersCount} filter
                  {activeFiltersCount !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>
          <div className="flex justify-between items-center min-w-0">
            {/* check-deprecations-disable-next-line */}
            <Tabs
              tabs={[
                ...(type.isSDK ? [] : [AccountStatusVariant.NeedToReport]),
                AccountStatusVariant.Active,
                AccountStatusVariant.Pending,
                AccountStatusVariant.Unsubscribed,
                AccountStatusVariant.All
              ]}
              displayMap={{
                [AccountStatusVariant.All]: "All",
                [AccountStatusVariant.Unsubscribed]: "Unsubscribed",
                [AccountStatusVariant.Active]: "Active",
                [AccountStatusVariant.Pending]: (
                  <>
                    Pending{" "}
                    {(pendingCountMeta.data || 0) > 0 && (
                      <div className="flex items-center justify-center rounded bg-danger-500 text-[10px] text-background-1000 px-2">
                        {pendingCountMeta.data}
                      </div>
                    )}
                  </>
                ),
                [AccountStatusVariant.NeedToReport]: (
                  <>
                    Need to report{" "}
                    {(needToReportCountMeta.data || 0) > 0 && (
                      <div className="flex items-center justify-center rounded bg-danger-500 text-[10px] text-background-1000 px-2">
                        {needToReportCountMeta.data}
                      </div>
                    )}
                  </>
                )
              }}
              selected={searchParams.type}
              setActiveTab={handleTabChange}
            />
          </div>
        </div>
        <div className="flex self-end items-center gap-4 shrink-0">
          <Show if={showBulkActions && permissions.boomReportWriteAccess}>
            <Button
              variant={ButtonVariant.TEXT}
              size={ButtonSize.XS}
              onClick={() => {
                const status =
                  selectDefaultValue === PaymentStatuses.NO_DATA
                    ? PaymentStatuses.PAID_ON_TIME
                    : PaymentStatuses.NO_DATA;
                setSelectDefaultValue(status);
                setSelectValues([]);
              }}
            >
              {selectDefaultValue === PaymentStatuses.NO_DATA
                ? "Mark all as paid on-time"
                : "Mark all as no data"}
            </Button>
          </Show>
          <div className="flex flex-col items-end gap-1">
            <Button
              variant={ButtonVariant.BASE}
              size={ButtonSize.SM}
              HugeIcon={Download04Icon}
              loading={isDownloadingCSV}
              onClick={() => {
                setIsDownloadingCSV(true);
                setDownloadProgress(null);
                handleDownloadCSV().finally(() => {
                  setIsDownloadingCSV(false);
                  setDownloadProgress(null);
                });
              }}
            >
              Download CSV
            </Button>
            <Show
              if={
                isDownloadingCSV &&
                downloadProgress !== null &&
                downloadProgress.total > 0
              }
            >
              <span className="text-xs text-content-700">
                Loaded {downloadProgress?.loaded ?? 0} of{" "}
                {downloadProgress?.total ?? 0} pages…
              </span>
            </Show>
          </div>
          <CSVLink
            ref={ref}
            data={csvData}
            filename={`rent-reporting-${DateTime.now().toFormat("MM-dd-yyyy HH:mm:ss")}.csv`}
            target="_blank"
            className="hidden"
          />
          <Show if={showBulkActions && permissions.boomReportWriteAccess}>
            <div className="h-10 w-px bg-alpha-40" />
            <Button
              variant={ButtonVariant.PRIMARY}
              size={ButtonSize.SM}
              Icon={CheckDone01}
              onClick={() => setShowReportModal(true)}
              loading={isReportingAllRenters}
            >
              Report all
            </Button>
          </Show>
        </div>
      </div>

      <SubscribersTable
        rentersData={subscribersMeta.data?.items ?? []}
        hasSearchParams={!!debouncedParams}
        selectDefaultValue={selectDefaultValue}
        selectValues={selectValues}
        setSelectValues={setSelectValues}
        isLoading={subscribersMeta.isFetching || isReportingAllRenters}
        sorted={sorted}
        setSorted={setSorted}
        activeTab={searchParams.type as AccountStatusVariant}
        isReportingSingleRenter={isReportingSingleRenter}
        reportSingleRenter={reportSingleRenter}
        pagination={
          <div className="flex items-center justify-between min-w-0">
            <p className="text-sm text-content-300 font-medium min-w-0">
              {subscribersMeta.data?.total_count}{" "}
              {subscribersMeta.data?.total_count === 1 ? "renter" : "renters"}
              {searchParams.type === AccountStatusVariant.NeedToReport ? (
                <>
                  , (<span className="font-bold">{paidOnTime || 0}</span> paid
                  on-time,{" "}
                  <span className="font-bold">
                    {(needToReportCountMeta.data ?? 0) - paidOnTime || 0}
                  </span>{" "}
                  late payment)
                </>
              ) : null}
            </p>
            <Pagination
              className="justify-end shrink-0 !w-fit"
              total={subscribersMeta.data?.page_count as number}
              current={Number(searchParams.page)}
              onChange={(e) =>
                setSearchParams({ ...searchParams, page: String(e) })
              }
            />
          </div>
        }
      />
      <ReportModal
        opened={showReportModal}
        onClose={() => setShowReportModal(false)}
        onReportAll={handleReportAll}
        paidOnTime={paidOnTime}
        needToReportCount={needToReportCountMeta.data ?? 0}
      />
      <FilterDrawer
        showFilters={showFilters}
        setShowFilters={setShowFilters}
        filters={filters}
        onFiltersChange={setFilters}
        showEnrollmentStatusFilter={isPendingTab || isAllTab}
        isSDK={type.isSDK}
        onApply={() => {
          setSearchParams({ ...searchParams, page: "1" });
        }}
        onCancel={() => {}}
      />
    </Layout>
  );
};
